import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import ColorCircle from './ColorCircle';

export const defaultColors = ['#53C433', '#FF6A7E', '#65A6FF', '#FFE749'];

const colorNames: Record<string, string> = {
  '#53C433': 'Green',
  '#FF6A7E': 'Pink',
  '#65A6FF': 'Blue',
  '#FFE749': 'Yellow',
};

type ColorSelectorProps = {
  defaultColor?: string;
  selectedColor?: string;
  onSelectColor?: (color: string) => void;
  overrideColors?: string[];
};

const ColorSelector = ({
  defaultColor,
  selectedColor,
  onSelectColor,
  overrideColors,
}: ColorSelectorProps) => {
  const [internalColor, setInternalColor] = useState<string | null>(defaultColor ?? null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [isComponentFocused, setIsComponentFocused] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const colors = overrideColors ?? defaultColors;

  useEffect(() => {
    if (selectedColor) {
      setInternalColor(selectedColor);

      const selectedIndex = colors.findIndex(
        (color) => color.toLocaleUpperCase() === selectedColor.toLocaleUpperCase(),
      );
      if (selectedIndex >= 0) {
        setFocusedIndex(selectedIndex);
      }
    } else if (internalColor) {
      setInternalColor(null);
    }
  }, [selectedColor, colors]);

  const handleColorClick = (color: string) => {
    onSelectColor?.(color);
    if (!selectedColor) setInternalColor(color);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (focusedIndex === -1 && e.key !== 'Tab') {
      setFocusedIndex(0);
      return;
    }

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % colors.length);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + colors.length) % colors.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleColorClick(colors[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        containerRef.current?.blur();
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    setIsComponentFocused(true);
    if (focusedIndex === -1) {
      const selectedIndex = colors.findIndex(
        (color) =>
          color.toLocaleUpperCase() ===
          (selectedColor?.toLocaleUpperCase() || internalColor?.toLocaleUpperCase()),
      );
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setFocusedIndex(-1);
      setIsComponentFocused(false);
    }
  };

  const getColorName = (color: string): string => {
    return colorNames[color] || color;
  };

  return (
    <Box
      ref={containerRef}
      role="radiogroup"
      aria-label="Color selection"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexShrink: 0,
        outline: 'none',
        padding: '2px',
        borderRadius: '4px',
        '&:focus-visible': {
          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.4)',
        },
      }}
    >
      {colors.map((color, index) => {
        const isSelected =
          color.toLocaleUpperCase() ===
          (selectedColor?.toLocaleUpperCase() || internalColor?.toLocaleUpperCase());

        return (
          <ColorCircle
            key={color}
            id={`color-option-${index}`}
            color={color}
            isSelected={isSelected}
            isFocused={isComponentFocused && index === focusedIndex}
            aria-label={`${getColorName(color)} color${isSelected ? ', selected' : ''}`}
            onClick={() => {
              handleColorClick(color);
              setFocusedIndex(index);
            }}
          />
        );
      })}
    </Box>
  );
};

export default ColorSelector;
